<template>
  <v-row dense class="lla-stats">
    <v-col
      v-for="(item, i) in items"
      :key="i"
      cols="12"
      sm="6"
      md="3"
    >
      <div class="lla-stat" :style="{ '--accent': accentFor(item, i) }">
        <div class="lla-stat__head">
          <span class="lla-stat__mark">
            <v-icon size="18">{{ item.icon || defaultIcons[i] || 'insights' }}</v-icon>
          </span>
          <span class="lla-stat__label">{{ item.title }}</span>
        </div>

        <div class="lla-stat__value">{{ formatValue(item.quantity) }}</div>

        <div v-if="item.hint" class="lla-stat__hint">{{ item.hint }}</div>
      </div>
    </v-col>
  </v-row>
</template>

<script>
// Paleta categórica validada contra la superficie oscura (#1c1826) con
// scripts/validate_palette.js: banda de luminosidad, chroma, separación CVD,
// piso de visión normal y contraste — las 5 pasan.
// El color acompaña a la etiqueta de texto, nunca la reemplaza.
const ACCENTS = ["#d95926", "#3987e5", "#c98500", "#9085e9"];

export default {
  name: "Indicadores",
  props: {
    items: { type: Array, default: () => [] },
  },
  data: () => ({
    defaultIcons: ["military_tech", "workspace_premium", "emoji_events", "diamond"],
  }),
  methods: {
    accentFor(item, i) {
      // `accent` explícito gana; si no, se asigna por posición fija (nunca cíclica
      // de forma arbitraria: el orden de los items es estable).
      return item.accent || ACCENTS[i % ACCENTS.length];
    },
    formatValue(v) {
      const n = Number(v);
      if (!isFinite(n)) return v == null ? "—" : String(v);
      // Compacta valores grandes para que el número no desborde la tarjeta.
      if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
      if (Math.abs(n) >= 10000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
      return n.toLocaleString("es-UY");
    },
  },
};
</script>

<style scoped>
.lla-stats {
  margin: 0;
}

.lla-stat {
  position: relative;
  height: 100%;
  padding: 16px 18px 18px;
  background: var(--lla-surface-1, #1c1826);
  border: 1px solid var(--lla-border, rgba(255, 255, 255, 0.08));
  border-radius: var(--lla-radius, 10px);
  overflow: hidden;
  transition: border-color 160ms ease, transform 160ms ease;
}

/* Barra de acento: identidad de la categoría, sin teñir toda la tarjeta */
.lla-stat::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: var(--accent);
}

.lla-stat:hover {
  border-color: rgba(255, 255, 255, 0.16);
  transform: translateY(-1px);
}

.lla-stat__head {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 10px;
}

.lla-stat__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  flex: none;
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--accent);
}

/* Fallback para navegadores sin color-mix() */
@supports not (background: color-mix(in srgb, red 10%, transparent)) {
  .lla-stat__mark {
    background: rgba(255, 255, 255, 0.08);
  }
}

.lla-stat__mark >>> .v-icon {
  color: var(--accent) !important;
}

/* La etiqueta usa color de texto, no el color de la serie */
.lla-stat__label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--lla-text-muted, rgba(255, 255, 255, 0.62));
  line-height: 1.2;
}

/* Cifras proporcionales (no tabulares): un número suelto y grande con
   tabular-nums se ve espaciado de más. */
.lla-stat__value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--lla-text, rgba(255, 255, 255, 0.94));
  font-variant-numeric: proportional-nums;
}

.lla-stat__hint {
  margin-top: 4px;
  font-size: 0.76rem;
  color: var(--lla-text-faint, rgba(255, 255, 255, 0.42));
}

@media (max-width: 600px) {
  .lla-stat__value {
    font-size: 1.7rem;
  }
}
</style>
